import type { FormInstance, TableColumnsType } from 'antd'
import type { RenderExpandIconProps } from 'rc-table/lib/interface'

import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Card, Col, Form, Input, Row, Space, Table } from 'antd'
import cx from 'classnames'
import { useCallback, useReducer } from 'react'

import $api from '../../API'
import { Permission } from '../../domain/permission.enum'
import { useAppSelector } from '../../store'

const columns: TableColumnsType<Location> = [
  {
    title: 'Key',
    key: 'key',
    dataIndex: 'key'
  },
  {
    title: 'Name',
    key: 'name',
    dataIndex: 'name'
  },
  {
    title: 'Short Name',
    key: 'short_name',
    dataIndex: 'short_name'
  },
  {
    title: 'Location',
    key: 'location',
    dataIndex: 'location'
  }
]

interface Location {
  id: string

  key: string
  name: string
  short_name: string
  location: string

  created_at: string
  updated_at: string
}

interface FormValues {
  name: string
  short_name: string
  location: string
}

type MutationPayload = Pick<Location, 'id' | 'name' | 'short_name' | 'location'>

interface State {
  expandedRow: {
    id?: string
  }
}

type Action = { type: 'set-expanded-row-id'; payload: string } | { type: 'clear-expanded-row-id' }

const initialState: State = {
  expandedRow: {
    id: undefined
  }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'set-expanded-row-id': {
      return {
        ...state,
        expandedRow: {
          ...state.expandedRow,
          id: action.payload
        }
      }
    }

    case 'clear-expanded-row-id': {
      return {
        ...state,
        expandedRow: {
          ...state.expandedRow,
          id: undefined
        }
      }
    }

    default: {
      return state
    }
  }
}

export function LocationsListPage(): JSX.Element {
  const [form] = Form.useForm<FormValues>()
  const [state, dispatch] = useReducer(reducer, initialState)
  const user = useAppSelector(state => state.app.user)

  const query = useQuery<unknown, unknown, Location[]>({
    queryKey: ['locations'],

    async queryFn() {
      const response = await $api.get('/locations')

      return response.data
    }
  })

  const mutation = useMutation({
    async mutationFn({ id, ...payload }: MutationPayload) {
      try {
        await $api.put(`/locations/${id}`, payload)
        dispatch({
          type: 'clear-expanded-row-id'
        })

        void query.refetch()
      } catch {
        throw new Error('Failed to update location')
      }
    }
  })

  const handleRowExpand = useCallback(
    (expanded: boolean, location: Location) => {
      form.resetFields()

      if (!expanded) {
        dispatch({
          type: 'clear-expanded-row-id'
        })

        return
      }

      form.setFieldsValue({
        name: location.name,
        short_name: location.short_name,
        location: location.location
      })

      dispatch({
        type: 'set-expanded-row-id',
        payload: location.id
      })
    },
    [form]
  )

  const handleSave = useCallback(() => {
    const id = state.expandedRow.id
    const values = form.getFieldsValue()

    if (id == null) {
      return
    }

    mutation.mutate({
      id,
      ...values
    })
  }, [form, mutation, state.expandedRow.id])

  const handleCancel = useCallback(() => {
    form.resetFields()
    dispatch({
      type: 'clear-expanded-row-id'
    })
  }, [form])

  return (
    <Card
      className='w-full rounded-xl'
      bodyStyle={{
        padding: '0'
      }}
    >
      <Table
        size='small'
        rowKey='id'
        dataSource={query.data ?? []}
        loading={query.isLoading}
        columns={columns}
        expandable={{
          rowExpandable() {
            return user.permissions.includes(Permission.WriteLocations)
          },
          expandedRowKeys: typeof state.expandedRow.id === 'string' ? [state.expandedRow.id] : [],
          expandedRowRender(location) {
            return (
              <ExpandedRow
                form={form}
                location={location}
                isSubmitting={mutation.isLoading}
                onSave={handleSave}
                onCancel={handleCancel}
              />
            )
          },
          expandIcon: ExpandIcon,
          onExpand: handleRowExpand,
          columnWidth: '5%'
        }}
        pagination={false}
      />
    </Card>
  )
}

interface ExpandedRowProps {
  form: FormInstance<FormValues>
  location: Location
  isSubmitting: boolean
  onSave: () => void
  onCancel: () => void
}

function ExpandedRow({
  form,
  location,
  isSubmitting,
  onSave,
  onCancel
}: ExpandedRowProps): JSX.Element {
  return (
    <div className='p-4'>
      <div className='text-gray-400'>
        <Form
          form={form}
          layout='horizontal'
          className='p-4'
          colon={false}
          labelCol={{
            className: 'font-semibold',
            span: 3
          }}
          wrapperCol={{
            span: 21
          }}
        >
          <Form.Item label='Key'>
            <span className='ant-form-text'>{location.key}</span>
          </Form.Item>

          <Form.Item name='short_name' label='Short Name' required>
            <Input size='large' className='w-full rounded-md' placeholder='E.g. NAA' />
          </Form.Item>

          <Form.Item name='name' label='Name' required>
            <Input
              size='large'
              className='w-full rounded-md'
              placeholder='E.g. North American Administrators'
            />
          </Form.Item>

          <Form.Item name='location' label='Location' required>
            <Input size='large' className='w-full rounded-md' placeholder='E.g. Tennessee' />
          </Form.Item>

          <Row>
            <Col span={24} className='flex justify-end'>
              <Space>
                <Button
                  size='large'
                  type='link'
                  htmlType='reset'
                  className='rounded-md'
                  onClick={event => {
                    event.preventDefault()
                    onCancel()
                  }}
                >
                  Cancel
                </Button>

                <Button
                  size='large'
                  type='primary'
                  htmlType='submit'
                  disabled={isSubmitting}
                  onClick={event => {
                    event.preventDefault()
                    onSave()
                  }}
                >
                  Save
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  )
}

function ExpandIcon(props: RenderExpandIconProps<Location>): JSX.Element | null {
  if (!props.expandable) {
    return null
  }

  return (
    <button
      className={cx('-ml-1 p-2 flex items-center justify-center transition-opacity translate-x-2', {
        'opacity-50 hover:opacity-75': !props.expanded,
        'opacity-100': props.expanded
      })}
      onClick={e => {
        props.onExpand(props.record, e)
      }}
    >
      {props.expanded && <CaretDownOutlined />}

      {!props.expanded && <CaretRightOutlined />}
    </button>
  )
}
